'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useProfileStrategies } from 'src/hooks/strategy';
import { useLogging } from 'src/context/logging';
import { useGetEngageStrategyListMembersQuery } from 'src/api/redux';
import {
  useGetStrategyMetricsQuery,
  useGetEngageStrategyDetailsQuery,
} from 'src/api/redux/strategy';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { faClipboardList } from '@fortawesome/pro-regular-svg-icons';
import { CircularProgress, Checkbox, Button, AddToListModal } from 'src/components';
import { PulseButton } from 'src/components/PulseUI';
import { STRATEGY_TYPES } from 'src/components/Strategy/constants';
import { StrategyMemberCard } from 'src/components/Strategy/shared';
import { useEngageStrategyConnector } from 'src/components/Profiles/shared/widgets/hooks/useEngageStrategyConnector';
import { getProfileUrl } from 'src/utils/profileUrl';

// Component to display engage strategy members
interface EngageStrategySectionProps {
  engageStrategy: any;
  providerId: string | number;
  providerType?: string | null;
  strategyType: string;
  chainId?: string | number | null;
}

const EngageStrategySection: React.FC<EngageStrategySectionProps> = ({
  engageStrategy,
  providerId,
  providerType,
  strategyType,
  chainId: chainIdProp,
}) => {
  const router = useRouter();
  const log = useLogging();
  const { closeDrawer } = useDrawer();

  // Selection state from Redux (shared with profile strategy pages)
  const {
    selectedProviders,
    setSelectedProviders,
    clearSelectedProviders,
  } = useProfileStrategies();

  // Local UI state
  const [currentPageSelected, setCurrentPageSelected] = useState(false);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);

  // Chain ID for navigation — passed from parent
  const chainId = chainIdProp ?? null;

  const strategyId = engageStrategy?.id?.toString() ?? '';
  const providerIdStr = providerId?.toString() ?? '';

  const handleMapClick = () => {
    if (!providerIdStr || !chainId) return;

    // Close the drawer
    closeDrawer('profile');

    // Navigate to the full profile view with strategy tab
    router.push(
      getProfileUrl(providerType || 'hcp', providerIdStr, `strategy/${chainId}`)
    );
  };

  // Check connector status before fetching members
  const {
    connectorData,
    isConnectorLoading,
    isConnector404,
    isConnectorExecuting,
    isProcessing,
    isExecuting,
    handleExecuteStrategy,
  } = useEngageStrategyConnector({
    strategyId,
    providerId: providerIdStr,
  });

  const isConnectorReady = connectorData?.status === 'READY';
  const skipDataQueries = !strategyId || !providerIdStr || !isConnectorReady;

  // Fetch strategy details to get direction
  const { data: engageStrategyDetails } = useGetEngageStrategyDetailsQuery(
    strategyId,
    { skip: !strategyId }
  );

  // Extract direction from query_plan
  const direction = React.useMemo(() => {
    const planDirection = engageStrategyDetails?.query_plan?.steps?.[0]?.params?.direction;
    return typeof planDirection === 'string' && planDirection ? planDirection : null;
  }, [engageStrategyDetails]);

  // Fetch strategy metrics to determine the correct sort field
  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    {
      strategyType: 'engage',
      strategyId,
    },
    {
      skip: skipDataQueries,
    }
  );

  // Determine the sort metric from the first metric's first display option
  const sortMetric = React.useMemo(() => {
    if (!strategyMetrics?.results?.length) return null;

    const firstMetric = strategyMetrics.results[0];
    const firstDisplayOption = firstMetric.display_options?.[0]?.code;

    return firstDisplayOption || null;
  }, [strategyMetrics?.results]);

  const {
    data: membersData,
    isLoading: isMembersLoading,
    isError,
    error: membersError,
  } = useGetEngageStrategyListMembersQuery(
    {
      strategyId,
      provider_id: providerIdStr,
      page_size: 10, // Show top 10
      sort: sortMetric ? 'metric' : 'date_added',
      order: 'desc',
      ...(sortMetric && { sort_metric: sortMetric }),
    },
    {
      skip: skipDataQueries,
    }
  );

  const members = useMemo(
    () => membersData?.results || [],
    [membersData?.results]
  );

  // Clear selected providers when the section unmounts (e.g. drawer closes)
  useEffect(() => {
    return () => {
      clearSelectedProviders();
    };
  }, [clearSelectedProviders]);

  // Track whether all items on current page are selected
  useEffect(() => {
    if (members.length === 0) {
      setCurrentPageSelected(false);
      return;
    }
    const allCurrentPageSelected = members.every((member: any) =>
      (selectedProviders || []).some(
        (p: MedScout.Opportunity) => p.provider_id === member.provider_id
      )
    );
    setCurrentPageSelected(allCurrentPageSelected);
  }, [members, selectedProviders]);

  // Handle select all on current page
  const handleSelectAllResults = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const isChecked = e.target.checked;
      setCurrentPageSelected(isChecked);

      if (isChecked) {
        log.event('strategyMultiSelectModeActivated');
        // Add current page results to existing selections, avoiding duplicates
        const currentSelected = selectedProviders || [];
        const newSelections = [...currentSelected];
        members.forEach((member: any) => {
          if (
            !currentSelected.some(
              (p: MedScout.Opportunity) => p.provider_id === member.provider_id
            )
          ) {
            newSelections.push(member);
          }
        });
        setSelectedProviders(newSelections);
      } else {
        // Only remove the current page items from selections
        setSelectedProviders(
          (selectedProviders || []).filter(
            (provider: MedScout.Opportunity) =>
              !members.some(
                (member: any) => member.provider_id === provider.provider_id
              )
          )
        );
      }
    },
    [members, log, selectedProviders, setSelectedProviders]
  );

  // Handle individual checkbox toggle
  const handleCheckboxToggle = useCallback(
    (member: any) => {
      const currentSelected = selectedProviders || [];
      const isAlreadySelected = currentSelected.some(
        (p: MedScout.Opportunity) => p.provider_id === member.provider_id
      );

      if (isAlreadySelected) {
        setSelectedProviders(
          currentSelected.filter(
            (p: MedScout.Opportunity) => p.provider_id !== member.provider_id
          )
        );
      } else {
        setSelectedProviders([...currentSelected, member]);
      }
    },
    [selectedProviders, setSelectedProviders]
  );

  // Handle add to list button click
  const handleAddToLists = useCallback(() => {
    setShowAddToListDialog(true);
  }, []);

  // Handle successful add to list
  const handleAddToListSuccess = useCallback(
    (result: { addedCount: number; targetListName: string }) => {
      log.event('Providers added to list', {
        listName: result.targetListName,
        count: result.addedCount,
      });
      clearSelectedProviders();
    },
    [log, clearSelectedProviders]
  );

  const selectedCount = (selectedProviders || []).length;

  const isLoading = isConnectorLoading || isMembersLoading;

  const processingMessage =
    connectorData?.status === 'CREATED'
      ? 'Strategy queued...'
      : 'Running strategy...';

  // Show processing state when connector is executing
  if (isProcessing) {
    return (
      <section
        className="self-stretch flex flex-col gap-4"
        data-testid="engage-strategy-processing"
      >
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-bold text-primary">
            {engageStrategy.label}
          </h3>
          {engageStrategy.description && (
            <p className="text-sm text-neutral-600">
              {engageStrategy.description}
            </p>
          )}
        </div>
        <div
          className="flex flex-col items-center justify-center p-8 gap-4"
          role="status"
          aria-label="Strategy is processing"
        >
          <div className="flex gap-1.5">
            <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:0ms]" />
            <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:150ms]" />
            <span className="w-2 h-2 rounded-full bg-sky-500 animate-bounce [animation-delay:300ms]" />
          </div>
          <p className="text-sm font-medium text-neutral-500">
            {processingMessage}
          </p>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <div
        className="self-stretch flex justify-center items-center p-4"
        role="status"
        aria-label="Loading strategy members"
        data-testid="engage-strategy-loading"
      >
        <CircularProgress size="sm" color="neutral" />
      </div>
    );
  }

  // Show not-run state when connector doesn't exist for this provider
  if (isConnector404) {
    return (
      <section
        className="self-stretch flex flex-col gap-4"
        data-testid="engage-strategy-not-run"
      >
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-bold text-primary">
            {engageStrategy.label}
          </h3>
          {engageStrategy.description && (
            <p className="text-sm text-neutral-600">
              {engageStrategy.description}
            </p>
          )}
        </div>
        <div
          className="flex flex-col items-center justify-center p-8 text-center"
          role="status"
        >
          <div
            className="w-20 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm"
            aria-hidden="true"
          >
            <Icon
              icon={faClipboardList}
              className="text-neutral-400"
              size="xl"
            />
          </div>
          <p className="text-sm text-neutral-500 mb-4">
            This strategy has not been run for this provider yet.
          </p>
          <PulseButton
            variant="solid"
            color="primary"
            onClick={handleExecuteStrategy}
            loading={isExecuting}
            aria-label="Execute engage strategy"
            data-testid="engage-strategy-execute-button"
          >
            Execute Engage Strategy
          </PulseButton>
        </div>
      </section>
    );
  }

  // Handle actual errors (connector exists but members query failed)
  if (isError) {
    return (
      <section
        className="self-stretch flex flex-col gap-4"
        aria-labelledby={`engage-strategy-error-heading-${engageStrategy.id}`}
        data-testid="engage-strategy-error"
      >
        <div className="flex flex-col gap-1">
          <h3
            id={`engage-strategy-error-heading-${engageStrategy.id}`}
            className="text-lg font-bold text-primary"
          >
            {engageStrategy.label}
          </h3>
          {engageStrategy.description && (
            <p className="text-sm text-neutral-600">
              {engageStrategy.description}
            </p>
          )}
        </div>
        <div
          className="flex flex-col items-center justify-center p-8 text-center"
          role="alert"
          aria-live="assertive"
        >
          <div
            className="w-20 h-20 bg-gradient-to-br from-red-50 to-rose-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm"
            aria-hidden="true"
          >
            <Icon icon={faClipboardList} className="text-red-500" size="xl" />
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">
            Error Loading Results
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed">
            An error occurred while loading the results. If this issue
            persists, please contact your Customer Success team member for
            assistance.
          </p>
        </div>
      </section>
    );
  }

  // Handle empty results (no error, just no data)
  if (!members.length) {
    const emptyMessage =
      strategyType === STRATEGY_TYPES.PATIENT_JOURNEY
        ? 'No insights found'
        : 'No providers found';

    return (
      <section
        className="self-stretch flex flex-col gap-4"
        aria-labelledby={`engage-strategy-empty-heading-${engageStrategy.id}`}
        data-testid="engage-strategy-empty"
      >
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h3
            id={`engage-strategy-empty-heading-${engageStrategy.id}`}
            className="text-lg font-bold text-primary"
          >
            {engageStrategy.label}
          </h3>
          {engageStrategy.description && (
            <p className="text-sm text-neutral-600">
              {engageStrategy.description}
            </p>
          )}
        </div>
        {/* Empty state message */}
        <div
          className="flex flex-col items-center justify-center p-8 text-center"
          role="status"
        >
          <div
            className="w-20 h-20 bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm"
            aria-hidden="true"
          >
            <Icon
              icon={faClipboardList}
              className="text-neutral-400"
              size="xl"
            />
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">
            {emptyMessage}
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed">
            There are no results for this strategy at this time.
          </p>
        </div>
      </section>
    );
  }

  // Helper to handle member click navigation
  const handleMemberClick = (member: any) => {
    const profileType = member.type || 'hcp';
    const profileId = member.provider_id || member.id;
    router.push(getProfileUrl(profileType, profileId));
  };

  // Unified render for all strategy types using StrategyMemberCard
  return (
    <section
      className="self-stretch flex flex-col gap-4"
      aria-labelledby={`engage-strategy-heading-${engageStrategy.id}`}
      data-testid={`engage-strategy-section-${engageStrategy.id}`}
    >
      {/* Header */}
      <header className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h3
            id={`engage-strategy-heading-${engageStrategy.id}`}
            className="text-lg font-bold text-primary"
          >
            {engageStrategy.label}
          </h3>
          {engageStrategy.description && (
            <p className="text-sm text-neutral-600">
              {engageStrategy.description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleMapClick}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 border border-sky-50 rounded-full hover:bg-sky-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!chainId}
          aria-label={`View ${engageStrategy.label} on map`}
          data-testid="engage-strategy-map-button"
        >
          <Icon icon={faLocationDot} className="text-sky-900" size="xs" aria-hidden="true" />
          <span className="text-xs font-bold text-sky-900">Map</span>
        </button>
      </header>

      {/* Selection Bar */}
      <div
        className="self-stretch h-10 px-4 py-2 bg-neutral-100 rounded-xl border border-neutral-100 inline-flex justify-between items-center"
        role="toolbar"
        aria-label="Selection controls"
        data-testid="engage-strategy-selection-bar"
      >
        <div className="flex justify-start items-center gap-2.5">
          <Checkbox
            id={`select-all-${engageStrategy.id}`}
            size="sm"
            color="primary"
            checked={currentPageSelected}
            onChange={handleSelectAllResults}
            aria-label={
              currentPageSelected
                ? 'Deselect all providers on this page'
                : 'Select all providers on this page'
            }
            data-testid="engage-strategy-select-all-checkbox"
          />
          <label
            htmlFor={`select-all-${engageStrategy.id}`}
            className="text-center justify-start text-secondary text-sm font-normal font-['Inter'] leading-tight cursor-pointer"
          >
            Select Page
          </label>
        </div>
        {selectedCount > 0 && (
          <output
            className="text-secondary text-xs font-semibold"
            aria-live="polite"
            aria-atomic="true"
            data-testid="engage-strategy-selected-count"
          >
            {selectedCount} Selected
          </output>
        )}
      </div>

      {/* Members List */}
      <ul
        className="self-stretch flex flex-col gap-2"
        role="list"
        aria-label={`${engageStrategy.label} members`}
        data-testid="engage-strategy-members-list"
      >
        {members.map((member: any) => {
          const isChecked = (selectedProviders || []).some(
            (p: MedScout.Opportunity) => p.provider_id === member.provider_id
          );
          return (
            <li key={member.id || member.provider_id} role="listitem">
              <StrategyMemberCard
                member={member}
                type={strategyType}
                direction={direction}
                strategyMetrics={strategyMetrics}
                isChecked={isChecked}
                onSelect={() => handleMemberClick(member)}
                onCheckboxChange={() => handleCheckboxToggle(member)}
                showAddToList
              />
            </li>
          );
        })}
      </ul>

      {/* Add to List Modal */}
      <AddToListModal
        isOpen={showAddToListDialog}
        onClose={() => setShowAddToListDialog(false)}
        mode="add"
        providers={selectedProviders || []}
        onSuccess={handleAddToListSuccess}
      />

      {/* Sticky Add to List Button */}
      {selectedCount > 0 && (
        <div
          className="sticky bottom-0 left-0 right-0 bg-white border-t border-neutral-200 p-4 -mx-4 -mb-4 mt-2"
          data-testid="engage-strategy-add-to-list-footer"
        >
          <Button
            variant="solid"
            color="primary"
            fullWidth
            onClick={handleAddToLists}
            className="rounded-full"
            aria-label={`Add ${selectedCount} ${selectedCount > 1 ? 'providers' : 'provider'} to a list`}
            data-testid="engage-strategy-add-to-list-button"
          >
            {`Add ${selectedCount} ${selectedCount > 1 ? 'Providers' : 'Provider'} to List`}
          </Button>
        </div>
      )}
    </section>
  );
};

export default EngageStrategySection;
